import { create } from 'zustand';
import client from '@/api/client';
import type {
  Issue,
  PlanTask,
  DashboardData,
  TodayTasks,
  IssueFilters,
  IssueListType,
  CreateIssueDTO,
  UpdateIssueDTO,
  CreatePlanTaskDTO,
  UpdatePlanTaskDTO,
  CompleteTaskDTO,
  DelayTaskDTO,
  VerifyBreakpointDTO,
  ImportPreview,
} from '@/types';

interface IssueState {
  productIssues: Issue[];
  productionIssues: Issue[];
  dashboardData: DashboardData | null;
  todayTasks: TodayTasks | null;
  
  activeView: 'timing' | 'issues';
  activeTab: IssueListType;
  expandedIssueId: string | null;
  isLoading: boolean;
  error: string | null;
  
  filters: IssueFilters;
  
  setActiveView: (view: 'timing' | 'issues') => void;
  setExpandedIssueId: (issueId: string | null) => void;
  
  loadIssues: (projectId: string) => Promise<void>;
  loadDashboard: (projectId: string) => Promise<void>;
  loadTodayTasks: (projectId: string) => Promise<void>;
  createIssue: (issue: CreateIssueDTO) => Promise<Issue>;
  updateIssue: (id: string, data: UpdateIssueDTO) => Promise<void>;
  updateIssueLocally: (id: string, updates: Partial<Issue>) => void;
  deleteIssue: (id: string) => Promise<void>;
  
  addPlanTask: (issueId: string, task: CreatePlanTaskDTO) => Promise<PlanTask>;
  updatePlanTask: (taskId: string, data: UpdatePlanTaskDTO) => Promise<void>;
  updatePlanTaskLocally: (taskId: string, updates: Partial<PlanTask>) => void;
  deletePlanTask: (taskId: string) => Promise<void>;
  completeTask: (taskId: string, data: CompleteTaskDTO) => Promise<void>;
  delayTask: (taskId: string, data: DelayTaskDTO) => Promise<void>;
  
  verifyBreakpoint: (issueId: string, data: VerifyBreakpointDTO) => Promise<void>;

  // 整改措施 CRUD
  loadActionItems: (issueId: string) => Promise<void>;
  createActionItem: (item: CreateActionItemDTO) => Promise<ActionItem>;
  updateActionItem: (id: string, data: UpdateActionItemDTO) => Promise<void>;
  deleteActionItem: (id: string) => Promise<void>;
  syncActionItems: (issueId: string, items: CreateActionItemDTO[]) => Promise<void>;

  setFilters: (filters: IssueFilters) => void;
  setActiveTab: (tab: IssueListType) => void;
  resetFilters: () => void;
  
  importIssues: (file: File, issueType: IssueListType, projectId: string) => Promise<ImportPreview>;
  confirmImport: (issues: CreateIssueDTO[]) => Promise<void>;
}

const DEFAULT_FILTERS: IssueFilters = {
  status: [],
  type: [],
  riskStatus: [],
  owner: [],
  dateRange: { start: '', end: '' },
  breakpointStatus: 'all',
  keyword: '',
};

export const useIssueStore = create<IssueState>((set, get) => ({
  productIssues: [],
  productionIssues: [],
  dashboardData: null,
  todayTasks: null,
  
  activeView: 'timing',
  activeTab: 'product',
  expandedIssueId: null,
  isLoading: false,
  error: null,
  
  filters: DEFAULT_FILTERS,
  
  setActiveView: (view) => set({ activeView: view }),
  setExpandedIssueId: (issueId) => set({ expandedIssueId: issueId }),
  
  loadIssues: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await client.get('/issues', { params: { projectId } });
      const result = response.data;
      
      if (result.success) {
        const normalizeIssues = (issues: Issue[]) =>
          issues.map(i => ({ ...i, actionItems: i.actionItems || [] }));
        
        set({
          productIssues: normalizeIssues(result.data.productIssues || []),
          productionIssues: normalizeIssues(result.data.productionIssues || []),
        });
      }
    } catch (error) {
      set({ error: 'Failed to load issues' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  loadDashboard: async (projectId) => {
    try {
      const response = await client.get('/issues-dashboard', { params: { projectId } });
      const result = response.data;
      if (result.success) {
        set({ dashboardData: result.data });
      }
    } catch (error) {
      console.error('Load dashboard error:', error);
    }
  },
  
  loadTodayTasks: async (projectId) => {
    try {
      const response = await client.get('/issues/today-tasks', { params: { projectId } });
      const result = response.data;
      if (result.success) {
        set({ todayTasks: result.data });
      }
    } catch (error) {
      console.error('Load today tasks error:', error);
    }
  },
  
  createIssue: async (issue) => {
    set({ isLoading: true, error: null });
    try {
      const response = await client.post('/issues', issue);
      const result = response.data;
      if (result.success) {
        const newIssue = result.data;
        if (issue.issueType === 'product') {
          set((state) => ({ productIssues: [...state.productIssues, newIssue] }));
        } else {
          set((state) => ({ productionIssues: [...state.productionIssues, newIssue] }));
        }
        return newIssue;
      }
      throw new Error(result.error);
    } catch (error) {
      console.error('Create issue error:', error);
      set({ error: 'Failed to create issue' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  updateIssue: async (id, data) => {
    try {
      const response = await client.put('/issues', { id, ...data });
      const result = response.data;
      if (result.success) {
        const updatedIssue = result.data;
        set((state) => ({
          productIssues: state.productIssues.map((i) => (i.id === id ? updatedIssue : i)),
          productionIssues: state.productionIssues.map((i) => (i.id === id ? updatedIssue : i)),
        }));
        return;
      }
    } catch (error: any) {
      console.error('[issueStore updateIssue] 错误:', error.response?.data || error.message);
      set({ error: 'Failed to update issue' });
      throw error;
    }
  },

  updateIssueLocally: (id, updates) => {
    set((state) => ({
      productIssues: state.productIssues.map((i) =>
        i.id === id ? { ...i, ...updates } : i
      ),
      productionIssues: state.productionIssues.map((i) =>
        i.id === id ? { ...i, ...updates } : i
      ),
    }));
  },

  deleteIssue: async (id) => {
    try {
      const response = await client.delete('/issues', { params: { id } });
      const result = response.data;
      if (result.success) {
        set((state) => ({
          productIssues: state.productIssues.filter((i) => i.id !== id),
          productionIssues: state.productionIssues.filter((i) => i.id !== id),
        }));
      }
    } catch (error) {
      console.error('Delete issue error:', error);
      set({ error: 'Failed to delete issue' });
    }
  },
  
  addPlanTask: async (issueId, task) => {
    try {
      const response = await client.post('/issues-tasks', task, { params: { issueId } });
      const result = response.data;
      if (result.success) {
        const newTask = result.data;
        const updateIssues = (issues: Issue[]) =>
          issues.map((i) =>
            i.id === issueId ? { ...i, planTasks: [...i.planTasks, newTask] } : i
          );
        set((state) => ({
          productIssues: updateIssues(state.productIssues),
          productionIssues: updateIssues(state.productionIssues),
        }));
        return newTask;
      }
      throw new Error(result.error);
    } catch (error: any) {
      console.error('[issueStore addPlanTask] 错误:', error.response?.data || error.message);
      throw error;
    }
  },
  
  updatePlanTask: async (taskId, data) => {
    try {
      const response = await client.put('/issues-tasks', data, { params: { taskId } });
      const result = response.data;
      if (result.success) {
        const updatedTask = result.data;
        const updateIssues = (issues: Issue[]) =>
          issues.map((i) => ({
            ...i,
            planTasks: i.planTasks.map((t) => (t.id === taskId ? updatedTask : t)),
          }));
        set((state) => ({
          productIssues: updateIssues(state.productIssues),
          productionIssues: updateIssues(state.productionIssues),
        }));
      }
    } catch (error) {
      console.error('[issueStore] Update plan task error:', error);
      throw error;
    }
  },

  updatePlanTaskLocally: (taskId, updates) => {
    const updateIssues = (issues: Issue[]) =>
      issues.map((i) => ({
        ...i,
        planTasks: i.planTasks.map((t) =>
          t.id === taskId ? { ...t, ...updates } : t
        ),
      }));
    set((state) => ({
      productIssues: updateIssues(state.productIssues),
      productionIssues: updateIssues(state.productionIssues),
    }));
  },
  
  deletePlanTask: async (taskId) => {
    try {
      const response = await client.delete('/issues-tasks', { params: { taskId } });
      const result = response.data;
      if (result.success) {
        const updateIssues = (issues: Issue[]) =>
          issues.map((i) => ({
            ...i,
            planTasks: i.planTasks.filter((t) => t.id !== taskId),
          }));
        set((state) => ({
          productIssues: updateIssues(state.productIssues),
          productionIssues: updateIssues(state.productionIssues),
        }));
      }
    } catch (error) {
      console.error('Delete plan task error:', error);
    }
  },
  
  completeTask: async (taskId, data) => {
    try {
      const response = await client.post('/issues-task-action', { ...data, action: 'complete' }, { params: { taskId } });
      const result = response.data;
      if (result.success) {
        const updatedTask = result.data.task;
        const updateIssues = (issues: Issue[]) =>
          issues.map((i) => ({
            ...i,
            planTasks: i.planTasks.map((t) => (t.id === taskId ? updatedTask : t)),
          }));
        set((state) => ({
          productIssues: updateIssues(state.productIssues),
          productionIssues: updateIssues(state.productionIssues),
        }));
      }
    } catch (error) {
      console.error('Complete task error:', error);
    }
  },
  
  delayTask: async (taskId, data) => {
    try {
      const response = await client.post('/issues-task-action', { ...data, action: 'delay' }, { params: { taskId } });
      const result = response.data;
      if (result.success) {
        const { task, delayRecord } = result.data;
        const updateIssues = (issues: Issue[]) =>
          issues.map((i) => ({
            ...i,
            planTasks: i.planTasks.map((t) =>
              t.id === taskId
                ? { ...task, delayHistory: [...t.delayHistory, delayRecord] }
                : t
            ),
            isDelayed: true,
          }));
        set((state) => ({
          productIssues: updateIssues(state.productIssues),
          productionIssues: updateIssues(state.productionIssues),
        }));
      }
    } catch (error) {
      console.error('Delay task error:', error);
    }
  },
  
  verifyBreakpoint: async (issueId, data) => {
    try {
      const response = await client.post('/issues-verify', data, { params: { issueId } });
      const result = response.data;
      if (result.success) {
        const updatedIssue = result.data;
        set((state) => ({
          productIssues: state.productIssues.map((i) => (i.id === issueId ? updatedIssue : i)),
          productionIssues: state.productionIssues.map((i) => (i.id === issueId ? updatedIssue : i)),
        }));
      }
    } catch (error) {
      console.error('Verify breakpoint error:', error);
    }
  },

  loadActionItems: async (issueId) => {
    try {
      const response = await client.get('/issues-action-items', { params: { issueId } });
      const result = response.data;
      if (result.success) {
        const updateIssue = (issues: Issue[]) =>
          issues.map((i) =>
            i.id === issueId ? { ...i, actionItems: result.data } : i
          );
        set((state) => ({
          productIssues: updateIssue(state.productIssues),
          productionIssues: updateIssue(state.productionIssues),
        }));
      }
    } catch (error) {
      console.error('Load action items error:', error);
    }
  },

  createActionItem: async (item) => {
    try {
      const response = await client.post('/issues-action-items', item);
      const result = response.data;
      if (result.success) {
        const newItem = result.data;
        const updateIssue = (issues: Issue[]) =>
          issues.map((i) =>
            i.id === item.issueId ? { ...i, actionItems: [...i.actionItems, newItem] } : i
          );
        set((state) => ({
          productIssues: updateIssue(state.productIssues),
          productionIssues: updateIssue(state.productionIssues),
        }));
        return newItem;
      }
    } catch (error) {
      console.error('Create action item error:', error);
      throw error;
    }
  },

  updateActionItem: async (id, data) => {
    try {
      const response = await client.put('/issues-action-items', data, { params: { id } });
      const result = response.data;
      if (result.success) {
        const updatedItem = result.data;
        const updateIssue = (issues: Issue[]) =>
          issues.map((i) => ({
            ...i,
            actionItems: i.actionItems.map((item) =>
              item.id === id ? updatedItem : item
            ),
          }));
        set((state) => ({
          productIssues: updateIssue(state.productIssues),
          productionIssues: updateIssue(state.productionIssues),
        }));
      }
    } catch (error) {
      console.error('Update action item error:', error);
      throw error;
    }
  },

  deleteActionItem: async (id) => {
    try {
      const response = await client.delete('/issues-action-items', { params: { id } });
      const result = response.data;
      if (result.success) {
        const updateIssue = (issues: Issue[]) =>
          issues.map((i) => ({
            ...i,
            actionItems: i.actionItems.filter((item) => item.id !== id),
          }));
        set((state) => ({
          productIssues: updateIssue(state.productIssues),
          productionIssues: updateIssue(state.productionIssues),
        }));
      }
    } catch (error) {
      console.error('Delete action item error:', error);
      throw error;
    }
  },

  syncActionItems: async (issueId, items) => {
    try {
      const response = await client.post('/issues-action-items', { issueId, items });
      const result = response.data;
      if (result.success) {
        const updateIssue = (issues: Issue[]) =>
          issues.map((i) =>
            i.id === issueId ? { ...i, actionItems: result.data } : i
          );
        set((state) => ({
          productIssues: updateIssue(state.productIssues),
          productionIssues: updateIssue(state.productionIssues),
        }));
        return result.data;
      }
      return null;
    } catch (error: any) {
      console.error('[issueStore syncActionItems] 错误:', error.response?.data || error.message);
      throw error;
    }
  },

  setFilters: (filters) => set({ filters }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  resetFilters: () => set({ filters: DEFAULT_FILTERS }),
  
  importIssues: async (file, issueType, projectId) => {
    set({ isLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('issueType', issueType);
      formData.append('projectId', projectId);
      
      const response = await client.post('/issues-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = response.data;
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error);
    } catch (error) {
      console.error('Import issues error:', error);
      set({ error: 'Failed to import issues' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  confirmImport: async (issues) => {
    set({ isLoading: true, error: null });
    try {
      const response = await client.post('/issues/import/confirm', { issues });
      const result = response.data;
      
      if (result.success) {
        const projectId = result.data?.projectId || issues[0]?.projectId;
        
        if (projectId) {
          await get().loadIssues(projectId);
        }
      }
    } catch (error) {
      console.error('Confirm import error:', error);
      set({ error: 'Failed to confirm import' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
